import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDeviceAlerts } from '../hooks/useDeviceAlerts.js'
import { useAccessData } from '../hooks/useAccessData.js'
import { useAccessManagement } from '../hooks/useAccessManagement.js'
import { NOTIFICATION_TYPES, useNotificationViews } from '../hooks/useNotificationViews.js'
import { useCloudContext } from '../contexts/CloudProvider.jsx'

/**
 * Custom hook for handling click outside to close functionality
 * Consistent with UserMenu.jsx and DeviceSelector.jsx patterns
 */
const useOutsideClick = (ref, isOpen, onClose) => {
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose()
      }
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [ref, isOpen, onClose])
}

// Constants for better maintainability and performance
const ALERT_CONFIGS = {
  connectivity: { icon: 'bi-wifi-off', baseColor: 'text-warning' },
  performance: { icon: 'bi-speedometer', baseColor: 'text-info' },
  health: { icon: 'bi-heart-pulse', baseColor: 'text-warning' },
  fault: { icon: 'bi-exclamation-triangle', baseColor: 'text-danger' },
  default: { icon: 'bi-info-circle', baseColor: 'text-info' },
}

const SEVERITY_COLORS = {
  critical: 'text-danger',
  high: 'text-warning', 
  medium: 'text-info',
  low: 'text-secondary',
}

const SEVERITY_BADGES = {
  critical: 'bg-danger',
  high: 'bg-warning text-dark',
  medium: 'bg-info',
  low: 'bg-secondary',
}

/**
 * NotificationBell component with world-class design and consistent patterns
 * - Beautiful animated dropdown with gradient effects
 * - Enhanced visual hierarchy and spacing  
 * - Consistent with UserMenu, DeviceSelector, and LeftNavigation patterns
 * - Optimized performance with memoization
 * - Elegant hover effects and transitions
 */
const NotificationBell = memo(() => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  const { selectDevice } = useCloudContext()
  const { alerts, unreadCount, markAsRead, dismissAlert, markAllAsRead } = useDeviceAlerts()
  const { useAdminAccessRequests, useUserInvitations, getUserInfo } = useAccessData()
  const { acceptInvitation, declineInvitation } = useAccessManagement()
  const { data: accessRequests, isLoading: _accessRequestsLoading } = useAdminAccessRequests()
  const { data: userInvitations, isLoading: _invitationsLoading } = useUserInvitations()
  
  // Map of invitation ID to inviter display name
  const [inviterMap, setInviterMap] = useState({})
  
  // Use the consistent outside click hook
  useOutsideClick(dropdownRef, isOpen, () => setIsOpen(false))
  useEffect(() => {
    userInvitations?.forEach(inv => {
      if (inv.invitedBy && !inviterMap[inv.id]) {
        getUserInfo(inv.invitedBy).then(user => {
          const name = user?.displayName || user?.name || user?.email || inv.invitedBy
          setInviterMap(prev => ({ ...prev, [inv.id]: name }))
        })
      }
    })
  }, [userInvitations, getUserInfo, inviterMap])
  
  // Initialize notification views system
  const {
    isNotificationViewed,
    markNotificationAsViewed,
    markAllNotificationsAsViewed,
    getUnreadCount,
  } = useNotificationViews()
  
  // Calculate unread access requests using notification views
  const unreadAccessRequestCount = getUnreadCount(accessRequests || [], NOTIFICATION_TYPES.ACCESS_REQUEST)
  
  // Calculate unread invitations using notification views
  const unreadInvitationCount = getUnreadCount(userInvitations || [], NOTIFICATION_TYPES.INVITATION)
  
  // Calculate total unread count (alerts + access requests + invitations)
  const totalUnreadCount = unreadCount + unreadAccessRequestCount + unreadInvitationCount
  
  // Memoized timestamp formatter
  const formatTimestamp = useCallback((timestamp) => {
    // Handle null, undefined, or invalid timestamps
    if (!timestamp) return 'Unknown time'
    
    let date
    try {
      // Handle Firestore timestamp objects
      if (timestamp && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate()
      } else {
        date = new Date(timestamp)
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date'
      }
    } catch (error) {
      console.warn('Error parsing timestamp:', timestamp, error)
      return 'Invalid date'
    }
    
    const diffMs = Date.now() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    
    return `${Math.floor(diffHours / 24)}d ago`
  }, [])

  // Memoized alert config getter
  const getAlertConfig = useCallback((type, severity) => {
    const config = ALERT_CONFIGS[type] ?? ALERT_CONFIGS.default
    const color = SEVERITY_COLORS[severity] ?? config.baseColor
    return { ...config, color }
  }, [])

  // Memoized event handlers
  const handleAlertClick = useCallback(async (alert) => {
    if (!alert.read) await markAsRead(alert.id)
  }, [markAsRead])

  const handleDismissAlert = useCallback(async (alertId, event) => {
    event.stopPropagation()
    await dismissAlert(alertId)
  }, [dismissAlert])

  // Handle navigation to device access management
  const handleAccessRequestClick = useCallback(async (request) => {
    try {
      // Mark the access request as viewed
      await markNotificationAsViewed(request.id, NOTIFICATION_TYPES.ACCESS_REQUEST, {
        deviceId: request.deviceId,
      })
      
      // Select the device in the cloud context with navigation context
      await selectDevice(request.deviceId, 'notification-access-request')
      
      // Navigate to access management page for access requests
      navigate('/access-management')
      
      // Close the notification dropdown
      setIsOpen(false)
    } catch (error) {
      console.error('Failed to navigate to device:', error)
    }
  }, [selectDevice, navigate, markNotificationAsViewed])

  // Handle navigation to invitation acceptance
  const handleInvitationClick = useCallback(async (invitation) => {
    try {
      // Mark the invitation as viewed
      await markNotificationAsViewed(invitation.id, NOTIFICATION_TYPES.INVITATION, {
        deviceId: invitation.deviceId,
      })
      
      // Navigate to access management page for invitations
      await selectDevice(invitation.deviceId, 'notification-invitation')
      navigate('/access-management')
      setIsOpen(false)
    } catch (error) {
      console.error('Failed to handle invitation click:', error)
    }
  }, [markNotificationAsViewed, selectDevice, navigate])

  // Handle accepting an invitation from the notification bell
  const handleAcceptInvitation = useCallback(async (invitation, event) => {
    event.stopPropagation()
    try {
      await acceptInvitation(invitation.id)
      
      // Mark as viewed and close dropdown
      await markNotificationAsViewed(invitation.id, NOTIFICATION_TYPES.INVITATION, {
        deviceId: invitation.deviceId,
      })
      setIsOpen(false)
      
      // Success feedback - the invitation will disappear from the UI automatically
      // since the document gets deleted and real-time listeners will update
      
    } catch (error) {
      console.error('Failed to accept invitation:', error)
      // Keep dropdown open on error so user can try again
    }
  }, [acceptInvitation, markNotificationAsViewed])

  // Handle declining an invitation from the notification bell
  const handleDeclineInvitation = useCallback(async (invitation, event) => {
    event.stopPropagation()
    try {
      await declineInvitation(invitation.id)
      
      // Mark as viewed and close dropdown
      await markNotificationAsViewed(invitation.id, NOTIFICATION_TYPES.INVITATION, {
        deviceId: invitation.deviceId,
      })
      setIsOpen(false)
      
      // Success feedback - the invitation will disappear from the UI automatically
      
    } catch (error) {
      console.error('Failed to decline invitation:', error)
      // Keep dropdown open on error so user can try again
    }
  }, [declineInvitation, markNotificationAsViewed])

  // Enhanced mark all as read function
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      // Mark all device alerts as read (existing functionality)
      if (unreadCount > 0) {
        await markAllAsRead()
      }
      
      // Mark all access requests as read (existing functionality)
      if (unreadAccessRequestCount > 0) {
        await markAllNotificationsAsViewed(
          accessRequests || [], 
          NOTIFICATION_TYPES.ACCESS_REQUEST,
        )
      }
      
      // Mark all invitations as read (new functionality)
      if (unreadInvitationCount > 0) {
        await markAllNotificationsAsViewed(
          userInvitations || [], 
          NOTIFICATION_TYPES.INVITATION,
        )
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }, [markAllAsRead, markAllNotificationsAsViewed, accessRequests, userInvitations, unreadCount, unreadAccessRequestCount, unreadInvitationCount])

  // Memoized unread count display
  const unreadDisplay = useMemo(() => 
    totalUnreadCount > 99 ? '99+' : totalUnreadCount,
    [totalUnreadCount],
  )

  // Memoized toggle handler
  const handleToggle = useCallback(() => setIsOpen(prev => !prev), [])

  return (
    <div className="position-relative me-3" ref={dropdownRef}>
      <style>
        {`
          .notification-bell-world-class {
            position: relative;
            background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-tertiary) 100%);
            border: 1px solid var(--border-color-soft);
            color: var(--text-muted);
            font-size: 1.1rem;
            padding: 0.625rem 0.75rem;
            border-radius: 12px;
            transition: all 0.2s ease;
            cursor: pointer;
            min-width: 44px;
            max-height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            outline: none;
            user-select: none;
            box-shadow: 0 2px 4px var(--shadow-color-light);
          }
          
          .notification-bell-world-class:hover {
            color: var(--bs-primary);
            background-color: var(--nav-hover-bg);
            border-color: var(--bs-primary);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(var(--bs-primary-rgb), 0.15);
          }
          
          .notification-bell-world-class:active {
            transform: translateY(0);
          }
          
          .notification-bell-world-class.active {
            color: var(--bs-primary);
            background-color: var(--nav-hover-bg);
            border-color: var(--bs-primary);
            box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.1);
          }
          
          .notification-badge-world-class {
            position: absolute;
            top: -0.375rem;
            right: -0.375rem;
            background: linear-gradient(135deg, var(--status-faults-bg) 0%, var(--bs-danger) 100%);
            color: white;
            border-radius: 12px;
            font-size: 0.7rem;
            font-weight: 600;
            padding: 0.15rem 0.4rem;
            min-width: 20px;
            text-align: center;
            border: 2px solid white;
            box-shadow: 0 2px 8px rgba(var(--bs-danger-rgb), 0.3);
            animation: pulse-badge 2s infinite;
          }
          
          @keyframes pulse-badge {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          
          .notification-dropdown-world-class {
            position: absolute;
            top: calc(100% + 0.5rem);
            right: 0;
            background: white;
            border: 1px solid var(--border-color-soft);
            border-radius: 16px;
            box-shadow: 0 10px 25px var(--shadow-color-medium);
            min-width: 400px;
            max-width: 450px;
            max-height: 500px;
            z-index: 1050;
            animation: slideDown 0.2s ease-out;
            overflow: hidden;
          }
          
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .notification-header-world-class {
            padding: 1rem 1.25rem 0.75rem;
            background: linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-quaternary) 100%);
            border-bottom: 1px solid var(--border-color-soft);
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .notification-content-world-class {
            max-height: 350px;
            overflow-y: auto;
            padding: 0.5rem 0;
          }
          
          .notification-item-world-class {
            padding: 0.875rem 1.25rem;
            border: none;
            background: transparent;
            cursor: pointer;
            transition: all 0.2s ease;
            border-left: 3px solid transparent;
            margin: 0.125rem 0.5rem;
            border-radius: 8px;
            display: block;
            width: calc(100% - 1rem);
          }
          
          .notification-item-world-class:hover {
            background-color: var(--nav-hover-bg);
            border-left-color: var(--bs-primary);
            transform: translateX(2px);
          }
          
          .notification-item-world-class.unread {
            background: linear-gradient(135deg, var(--status-warning-bg) 0%, var(--bg-warning-light) 100%);
            border-left-color: var(--bs-warning);
          }
          
          .notification-item-world-class.unread:hover {
            background-color: var(--nav-hover-bg);
            border-left-color: var(--alert-warning-border);
          }
          
          .notification-empty-world-class {
            text-align: center;
            padding: 3rem 1.5rem;
            color: var(--text-muted);
          }
          
          .notification-footer-world-class {
            padding: 0.75rem 1.25rem;
            background: var(--bg-header-gradient);
            border-top: 1px solid var(--bg-tertiary);
            font-size: 0.8rem;
            color: var(--text-muted);
            text-align: center;
          }
          
          .notification-icon-world-class {
            width: 36px;
            height: 36px;
            min-width: 36px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 0.75rem;
          }
        `}
      </style>
      
      <button
        className={`notification-bell-world-class ${isOpen ? 'active' : ''}`}
        type="button"
        onClick={handleToggle}
        title="Notifications"
        aria-expanded={isOpen}
        aria-label={`Notifications${totalUnreadCount > 0 ? ` (${unreadDisplay} unread)` : ''}`}
      >
        <i className="bi bi-bell"></i>
        
        {/* Enhanced Unread count badge */}
        {totalUnreadCount > 0 && (
          <span className="notification-badge-world-class">
            {unreadDisplay}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown-world-class">
          <div className="notification-header-world-class">
            <h6 className="mb-0 fw-semibold text-dark d-flex align-items-center">
              <i className="bi bi-bell-fill me-2 text-primary"></i>
              Notifications
            </h6>
            {(unreadCount > 0 || unreadAccessRequestCount > 0 || unreadInvitationCount > 0) && (
              <button 
                className="btn btn-sm btn-outline-primary border-0"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                onClick={handleMarkAllAsRead}
              >
                <i className="bi bi-check2-all me-1"></i>
                Mark all read
              </button>
            )}
          </div>
          
          <div className="notification-content-world-class">
            {!alerts.length && !accessRequests?.length && !userInvitations?.length ? (
              <div className="notification-empty-world-class">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '2.5rem' }}></i>
                <div className="mt-2 fw-semibold">All caught up!</div>
                <small className="text-muted">No new notifications</small>
              </div>
            ) : (
              <>
                {/* Invitations section - Beautiful design */}
                {userInvitations?.map((invitation) => {
                  const isInvitationViewed = isNotificationViewed(invitation.id, NOTIFICATION_TYPES.INVITATION)
                  const inviter = inviterMap[invitation.id] || invitation.invitedByName || invitation.invitedByEmail || invitation.invitedBy || 'Unknown user'
                  
                  return (
                    <div
                      key={`invitation-${invitation.id}`}
                      className={`notification-item-world-class ${!isInvitationViewed ? 'unread' : ''}`}
                      onClick={() => handleInvitationClick(invitation)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="d-flex align-items-start">
                        <div className="notification-icon-world-class bg-success bg-opacity-10">
                          <i className="bi bi-envelope-heart text-success"></i>
                        </div>
                        <div className="flex-grow-1 min-width-0">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className="mb-0 fw-semibold text-truncate" style={{ maxWidth: '250px' }}>
                              Device Invitation
                            </h6>
                            {!isInvitationViewed && (
                              <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>New</span>
                            )}
                          </div>
                          <p className="mb-2 text-muted small lh-sm">
                            <strong>{inviter}</strong> invited you to access device <br />
                            <code className="text-primary">{invitation.deviceId}</code> as <em>{invitation.permission}</em>
                          </p>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              {formatTimestamp(invitation.invitedAt || invitation.createdAt)}
                            </small>
                            <div className="btn-group btn-group-sm" onClick={(e) => e.stopPropagation()}>
                              <button 
                                className="btn btn-sm btn-outline-success"
                                onClick={(e) => handleAcceptInvitation(invitation, e)}
                                title="Accept invitation"
                                style={{ padding: '0.2rem 0.4rem' }}
                              >
                                <i className="bi bi-check-lg"></i>
                              </button>
                              <button 
                                className="btn btn-sm btn-outline-danger"
                                onClick={(e) => handleDeclineInvitation(invitation, e)}
                                title="Decline invitation"
                                style={{ padding: '0.2rem 0.4rem' }}
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
                
                {/* Access requests section - Beautiful design */}
                {accessRequests?.map((request) => {
                  const isRequestViewed = isNotificationViewed(request.id, NOTIFICATION_TYPES.ACCESS_REQUEST)
                  
                  return (
                    <div
                      key={`request-${request.id}`}
                      className={`notification-item-world-class ${!isRequestViewed ? 'unread' : ''}`}
                      onClick={() => handleAccessRequestClick(request)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="d-flex align-items-start">
                        <div className="notification-icon-world-class bg-primary bg-opacity-10">
                          <i className="bi bi-person-plus text-primary"></i>
                        </div>
                        <div className="flex-grow-1 min-width-0">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className="mb-0 fw-semibold text-truncate" style={{ maxWidth: '250px' }}>
                              Access Request
                            </h6>
                            {!isRequestViewed && (
                              <span className="badge bg-primary ms-2" style={{ fontSize: '0.65rem' }}>New</span>
                            )}
                          </div>
                          <p className="mb-2 text-muted small lh-sm">
                            <strong>{request.requestedByName || request.requestedByEmail}</strong> requested <br />
                            <em>{request.permission}</em> access to this device
                          </p>
                          <small className="text-muted">
                            {formatTimestamp(request.requestedAt || request.createdAt)}
                          </small>
                        </div>
                      </div>
                    </div>
                  )
                })}
                
                {/* Device alerts section - Beautiful design */}
                {alerts.map((alert) => {
                  const config = getAlertConfig(alert.type, alert.severity)
                  const badgeClass = SEVERITY_BADGES[alert.severity] ?? 'bg-secondary'
                  
                  return (
                    <div
                      key={`alert-${alert.id}`}
                      className={`notification-item-world-class ${!alert.read ? 'unread' : ''}`}
                      onClick={() => handleAlertClick(alert)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="d-flex align-items-start">
                        <div className="notification-icon-world-class bg-warning bg-opacity-10">
                          <i className={`${config.icon} ${config.color}`}></i>
                        </div>
                        <div className="flex-grow-1 min-width-0">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className="mb-0 fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
                              {alert.title}
                            </h6>
                            <div className="d-flex align-items-center gap-1">
                              {!alert.read && (
                                <span className="badge bg-warning ms-2" style={{ fontSize: '0.65rem' }}>New</span>
                              )}
                              <span className={`badge ${badgeClass}`} style={{ fontSize: '0.65rem' }}>
                                {alert.severity}
                              </span>
                            </div>
                          </div>
                          <p className="mb-2 text-muted small lh-sm">{alert.message}</p>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              {formatTimestamp(alert.createdAt)}
                            </small>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={(e) => handleDismissAlert(alert.id, e)}
                              title="Dismiss alert"
                              style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </>
            )}
          </div>
          
          {/* Beautiful footer with stats */}
          {(!!alerts.length || !!accessRequests?.length || !!userInvitations?.length) && (
            <div className="notification-footer-world-class">
              {unreadInvitationCount > 0 && (
                <>
                  <i className="bi bi-envelope-heart text-success me-1"></i>
                  {unreadInvitationCount} invitation{unreadInvitationCount !== 1 ? 's' : ''}
                  {(unreadAccessRequestCount > 0 || alerts.length > 0) && ' • '}
                </>
              )}
              {unreadAccessRequestCount > 0 && (
                <>
                  <i className="bi bi-person-plus text-primary me-1"></i>
                  {unreadAccessRequestCount} access request{unreadAccessRequestCount !== 1 ? 's' : ''}
                  {alerts.length > 0 && ' • '}
                </>
              )}
              {alerts.length > 0 && (
                <>
                  <i className="bi bi-exclamation-triangle text-warning me-1"></i>
                  {alerts.length} alert{alerts.length !== 1 ? 's' : ''}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
})

NotificationBell.displayName = 'NotificationBell'

export default NotificationBell
